/**
 * Operator-facing incident-OWNERSHIP vocabulary — doctrine-locked (Ontario IMS
 * Guidance 2.0; docs/ontario-ims-doctrine-reference.md).
 *
 * Taking an incident is "Own / Owned" — a neutral plain-language handle for "Incident
 * Command established" (§4.1/§4.5), claiming no statutory authority. "Declare / Declared"
 * is RESERVED for declaration-of-emergency (§5.4) + authority-issued orders (evacuation)
 * and MUST NOT appear on an incident-ownership affordance. These constants are the single
 * source for that copy; `incidentVocabulary.lock.test.ts` pins them + a forbidden-term
 * guard (mirrors the default-view lock — changing them is founder authorization).
 */
export const OWN_INCIDENT_LABEL = 'Own Incident';
export const OWN_INCIDENT_BUSY_LABEL = 'Owning…';
export const OWN_INCIDENT_ARIA_LABEL = 'Own incident';
export const OWN_INCIDENT_HELP =
  'Not yet tracked. Owning opens the operational record — a stable incident number, ' +
  'operational periods, and the SITREP chain.';
export const OWN_INCIDENT_ERROR = 'Could not own the incident. Please retry.';

/** Success toast after taking ownership of an incident (keeps "now a tracked incident"). */
export function ownIncidentToast(incidentNumber: string): string {
  return `Incident ${incidentNumber} owned — now a tracked incident.`;
}

/**
 * W3 spine (#489) — structured INCIDENT COMMAND vocabulary (Ontario IMS §4). "Incident
 * Command" (§4.1 command established), "Lead Organization" (§4.5, the participant term),
 * "Assume Incident Command" (§4 — the responder assumes command). The forbidden drift
 * terms — an EOC-coordination officer label (§2.2/§2.9) and an N-owner ownership label
 * (§2.10 — unified command is ONE shared command) — are enumerated in the doctrine
 * reference and pinned (absent) by incidentVocabulary.lock.test.ts. Editing these
 * constants is founder authorization.
 */
export const INCIDENT_COMMAND_HEADING = 'Incident Command';
export const LEAD_ORGANIZATION_LABEL = 'Lead Organization';
export const ASSUME_COMMAND_LABEL = 'Assume Incident Command';
export const RELEASE_COMMAND_LABEL = 'Relinquish Command';

/**
 * W3 unified command + transfer (#492) — the §4.7 / §4.8 command-structure vocabulary, VERBATIM
 * Ontario IMS (docs/ontario-ims-doctrine-reference.md §1.2 / §2.9–§2.13; per-surface §4.1 = UI
 * strict verbatim). "Unified Command" (§4.7 — ONE shared command, NOT N owners), the single
 * "Lead — Operations Section Chief" (§4.7 "one operations section chief … with the greatest
 * jurisdictional or functional involvement"), "Operations branch" (§4.7 — the functional split
 * is branches under the one lead, not ownerships), "Transfer of Command" + "hand-over briefing"
 * (§4.8), "Signed agreement" (§4.7 "verbally stated and signed in the written IAP"), and the
 * §4.7 return path. The forbidden drift terms (co-owned / owns-suppression / owns-protection /
 * "unity of command" for shared command) are pinned ABSENT by incidentVocabulary.lock.test.ts.
 * Editing these constants is founder authorization.
 */
export const UNIFIED_COMMAND_HEADING = 'Unified Command';
export const LEAD_OPS_SECTION_CHIEF_LABEL = 'Lead — Operations Section Chief';
export const OPERATIONS_BRANCH_LABEL = 'Operations branch';
/**
 * #481 MUST-2 (two-sided §4.7 accept, migration 0147) — unified command is entered by MUTUAL
 * agreement, so it is an issued INVITE the counterparty ACCEPTS (never a unilateral join). The
 * issuer INVITEs; the invitee ACCEPTs. (Replaces the old one-click "Enter Unified Command", which
 * added a co-lead unilaterally.) "Invite" / "Accept" are plain verbs — they claim no reserved
 * doctrine phase and collide with nothing (§4.7 "Unified Command" is the verbatim doctrine term).
 */
export const INVITE_TO_UNIFIED_COMMAND_LABEL = 'Invite to Unified Command';
export const ACCEPT_UNIFIED_COMMAND_LABEL = 'Accept Unified Command';
/** The pending-invite banner label (parallel to TRANSFER_OF_COMMAND_LABEL for a pending transfer). */
export const UNIFIED_COMMAND_INVITE_LABEL = 'Unified Command invite';
export const RETURN_TO_SINGLE_COMMAND_LABEL = 'Return to Single Command';
export const TRANSFER_OF_COMMAND_LABEL = 'Transfer of Command';
export const HANDOVER_BRIEFING_LABEL = 'Hand-over briefing';
export const ACKNOWLEDGE_TRANSFER_LABEL = 'Acknowledge Transfer of Command';
export const SIGNED_AGREEMENT_LABEL = 'Signed agreement';

/**
 * #481 CLEANUP — the "who to reach" command seat is the INCIDENT COMMANDER (Ontario IMS App A
 * #37, verbatim), NEVER a "duty officer" (an EOC/PEOC COORDINATION seat, §2.2/§2.9 — the owner
 * commands, it does not coordinate). The #481 council's proposed "Responding Provincial Duty
 * Officer" panel would be a ❌ DOCTRINE-MISMATCH on a command surface; the shipped command block
 * names the officer the Incident Commander. Pinned (and pinned ABSENT of "duty officer") by
 * incidentVocabulary.lock.test.ts.
 */
export const INCIDENT_COMMANDER_LABEL = 'Incident Commander';

/**
 * OWNERSHIP-COORDINATION vocabulary (feat/ownership-coordination) — the CROSS-AGENCY common-picture
 * copy: every COP operator, in any agency, reads WHO holds command of a hazard and how to reach
 * them. VERBATIM Ontario IMS (docs/ontario-ims-doctrine-reference.md):
 *   • "Incident Command established — <Lead Organization>" — §1 lifecycle table (owned row) +
 *     §4.3 line 440; grounded App A #37 (Incident commander) / §4.5 + App A #74 (single command).
 *   • "Unified Command established — <A> · <B>" — §4.7 (one shared command, NOT N owners).
 *   • "Liaison Officer" — App A #46: "the primary contact for outside communities and organizations
 *     involved in supporting the incident response … including any requests for assistance." This
 *     is the DUTY line a non-owner reaches through (the IC rotates every operational period, §4.8).
 *
 * DELIBERATELY NOT "Owned"/"Incident Owner"/"Owning agency" on the CROSS-AGENCY line — "Owned" is
 * the shipped SINGLE-agency lifecycle chip (a signed §4.3 handle); reused cross-agency it begs
 * "owned by whom?", so the cross-agency line always NAMES the organization. Auto-scanned +
 * forbidden-guarded by ontarioImsVocabulary.lock.test.ts + incidentVocabulary.lock.test.ts.
 */
export const LIAISON_OFFICER_LABEL = 'Liaison Officer';
/** Compact "(primary contact)" annotation for the Liaison Officer duty line (App A #46). */
export const PRIMARY_CONTACT_SUFFIX = 'primary contact';
/** Attribution line — "Incident Command established — <Lead Organization>" (§4.5 / App A #37). */
export function incidentCommandEstablishedLabel(leadOrganization: string): string {
  return `${INCIDENT_COMMAND_HEADING} established — ${leadOrganization}`;
}
/** Unified variant — "Unified Command established — <A> · <B>" (§4.7). Rendering single-command
 *  copy on a unified incident is a ❌ DOCTRINE-MISMATCH, so this is MANDATORY when unified. */
export function unifiedCommandEstablishedLabel(leadOrganizations: string[]): string {
  const orgs = Array.isArray(leadOrganizations) ? leadOrganizations : [String(leadOrganizations)];
  return `${UNIFIED_COMMAND_HEADING} established — ${orgs.join(' · ')}`;
}
/** Compact map-hover attribution — names the organization + the incident's Response Level (§5),
 *  never "Owned" (the cross-agency line names the org). Response Level omitted when unassessed. */
export function ownershipHoverLabel(
  leadOrganization: string,
  responseLevel: number | null | undefined,
): string {
  const base = `${INCIDENT_COMMAND_HEADING} · ${leadOrganization}`;
  return responseLevel != null ? `${base} · Response Level ${responseLevel}` : base;
}
/** Jurisdiction-tier labels for a Lead Organization / declaring authority. */
export const JURISDICTION_TIER_LABELS: Record<string, string> = {
  municipal: 'Municipal',
  provincial: 'Provincial',
  first_nation: 'First Nation',
  federal: 'Federal',
};
export function jurisdictionTierLabel(kind: string | null | undefined): string {
  if (!kind) return '';
  return JURISDICTION_TIER_LABELS[kind] ?? kind.replace(/_/g, ' ');
}
/**
 * Declared state (statutory) — VERBATIM "Declaration of emergency" (EMCPA / PERP; the reserved
 * declaration-of-emergency domain, distinct from the incident Response Level). This is the correct
 * home for the reserved "Declar…" term — a declaration of emergency IS that domain. Names the
 * declaring authority's tier + the declaration number.
 */
export const DECLARATION_OF_EMERGENCY_LABEL = 'Declaration of emergency';
export function declaredStateLabel(
  authorityKind: string | null | undefined,
  declarationNumber: string | null | undefined,
): string {
  const tier = jurisdictionTierLabel(authorityKind);
  const parts = [DECLARATION_OF_EMERGENCY_LABEL];
  if (tier) parts.push(tier);
  if (declarationNumber) parts.push(declarationNumber);
  return parts.join(' · ');
}

/**
 * #481 CLEANUP — issuer WAYFINDING on the Transfer of Command / Unified Command invite surface.
 * A command change targets another commanding LEAD ORGANIZATION (provincial / municipal / First
 * Nation), NEVER the PEOC — reaching the PEOC is Declare · Notify · Request (#495/#497), a
 * coordination act, not a command hand-over (§4.2 command ≠ coordination). Descriptive copy;
 * names only verbatim doctrine terms + the shipped coordination surfaces.
 */
export const COMMAND_TRANSFER_WAYFINDING =
  'A command change goes to another commanding Lead Organization (provincial · municipal · First Nation). To reach the PEOC, use Declare · Notify · Request — not a command transfer (§4.2 command ≠ coordination).';

/**
 * #481 MUST-1 — the RECEIVER's inbound worklist of pending command changes addressed to their
 * organization (§4.8 transfers + §4.7 unified invites). A descriptive product grouping (no
 * reserved-doctrine claim); each row names its precise doctrine kind (below).
 */
export const COMMAND_INBOX_HEADING = 'Incoming command changes';
export const COMMAND_INBOX_SUBTEXT =
  'Transfers of Command and Unified Command invites addressed to your organization — open one to accept.';
export const INCOMING_TRANSFER_OF_COMMAND_LABEL = 'Incoming Transfer of Command';
export const INCOMING_UNIFIED_COMMAND_INVITE_LABEL = 'Invitation to Unified Command';
/** The precise per-kind label for one inbound command-change row. */
export function commandHandoverInboxLabel(kind: 'transfer' | 'unified_invite'): string {
  return kind === 'unified_invite'
    ? INCOMING_UNIFIED_COMMAND_INVITE_LABEL
    : INCOMING_TRANSFER_OF_COMMAND_LABEL;
}

/**
 * §4.7 Operations-branch labels for a command participation's functional_role. Descriptive
 * labels on participants (the functional split under the ONE lead) — NOT ownerships. Keyed by
 * the incident_command.functional_role FK values (packages/shared-types INCIDENT_FUNCTIONAL_ROLES).
 */
export const OPERATIONS_BRANCH_LABELS: Record<string, string> = {
  suppression: 'Suppression',
  community_protection: 'Community Protection',
  structure_protection: 'Structure Protection',
  evacuation: 'Evacuation',
  medical: 'Medical',
};
export function operationsBranchLabel(role: string | null): string | null {
  if (!role) return null;
  return OPERATIONS_BRANCH_LABELS[role] ?? role.replace(/_/g, ' ');
}

/**
 * R5 Area Command spine (#493) — operator-facing AREA COMMAND vocabulary, VERBATIM
 * Ontario IMS §4.6 ("Area command … set up to oversee the management of multiple sites
 * or large or escalating incidents which involve several incident management teams").
 * The grouped multi-site / multi-fire entity is an "Area Command" (Appendix A #2).
 *
 * ONTARIO IMS 2.0 DEFINES NO "AREA COMMANDER" ROLE (correction #498). "Area Commander" was a
 * US-ICS title imported in error by #493/#494 and has been REMOVED. Ontario names its person-
 * roles explicitly (incident commander, EOC director / EOC commander, section chiefs, liaison /
 * safety / emergency-information officers) and deliberately does NOT title an "area commander" —
 * the lead is the participating LEAD ORGANIZATIONS (§4.5) and their own incident management
 * teams, and "in some incidents, area command will be a form of unified command" (§4.6). The
 * Area Command therefore surfaces its NAME + its member incidents; each member keeps its OWN
 * Incident Command block (Lead Organization + Incident Commander, §4.5/§4.1). There is NO single
 * titled area-command lead on any operator surface.
 *
 * "Complex" / "complex incident" / "unified command" MUST NOT appear on any Area Command surface:
 * "Complex" (FIRE_COMPLEX_NAME) is BACKEND-ONLY (a later slice's membership-proposal signal), and
 * unified command (§4.7) is a DIFFERENT concept (one shared command on ONE incident, not oversight
 * across many). These constants are the single source for that copy; incidentVocabulary.lock.test.ts
 * pins them + a forbidden-term guard, and ontarioImsVocabulary.lock.test.ts forbids the US-ICS
 * "Area Commander" (+ PIO / "Unit Leader" / ICS-form drift) on every operator string. Changing
 * these constants is founder authorization.
 */
export const AREA_COMMAND_HEADING = 'Area Command';
export const ESTABLISH_AREA_COMMAND_LABEL = 'Establish Area Command';
export const PLACE_UNDER_AREA_COMMAND_LABEL = 'Place Under Area Command';
export const REMOVE_FROM_AREA_COMMAND_LABEL = 'Remove from Area Command';

/**
 * R5 Area Command Slice 2 (#494) — membership-PROPOSAL + child-membership copy, VERBATIM
 * Ontario IMS §4.6. The proposal is oversight GROUPING, never a fused "complex" or a
 * "merge": the FIRE_COMPLEX_NAME signal is backend-only (NEVER rendered — founder ruling
 * 2026-07-01) and the rationale reads "shared management grouping", not "shared complex".
 * "merge" / "combine" / "co-own" are forbidden alongside "Complex" / "unified command" on
 * any Area Command surface — incidentVocabulary.lock.test.ts pins these + the guard.
 */
/** Child-incident membership badge — `Under Area Command — <Area Command name>`. */
export const UNDER_AREA_COMMAND_PREFIX = 'Under Area Command';
export function underAreaCommandLabel(areaCommandName: string): string {
  return `${UNDER_AREA_COMMAND_PREFIX} — ${areaCommandName}`;
}
/** Proposal-card heading — ESTABLISH a new Area Command over N ungrouped fires. */
export function establishAreaCommandProposalPrompt(fireCount: number): string {
  return `${ESTABLISH_AREA_COMMAND_LABEL} over ${fireCount} ${fireCount === 1 ? 'fire' : 'fires'}?`;
}
/** Proposal-card heading — ADD N ungrouped fires to an existing adjacent Area Command. */
export function addToAreaCommandProposalPrompt(fireCount: number, areaCommandName: string): string {
  return `Add ${fireCount} ${fireCount === 1 ? 'fire' : 'fires'} to ${AREA_COMMAND_HEADING} — ${areaCommandName}?`;
}
/** Proposal rationale (server `kind` → operator copy). NEVER contains "complex" or the
 *  FIRE_COMPLEX_NAME value — the shared-grouping signal reads as a management grouping. */
export function areaCommandProposalRationale(
  kind: 'shared_grouping' | 'proximity',
  fireCount: number,
  spreadKm: number,
): string {
  return kind === 'shared_grouping'
    ? `shared management grouping · ${fireCount} fires`
    : `~${spreadKm} km apart, same hazard`;
}

/**
 * R5 Area Command Slice 3 (#507) — the Area Command COP MARKER + expand-panel copy,
 * gate-corrected at the 2026-07-02 IMS design gate (three independent runs against live
 * ontario.ca incl. a 4-seat panel, unanimous):
 *
 *  - The marker badge counts "+N INCIDENTS" — NEVER "+N members". "member(s)" appears
 *    nowhere in Ontario IMS §4 / Appendix A and collides with App A #85 *Unified area
 *    command* ("shared between two or more lead organizations or jurisdictions") and
 *    App A #39 IMT personnel; the grouped children are App A #35 *incidents*.
 *  - The expand-panel heading is "Incidents under area command (N)" — "under area
 *    command" is VERBATIM Ontario IMS phrasing (App A #85: "Management of an incident
 *    under area command is shared…"); the grouping concept is §4.6 / App A #2 ("set up
 *    to oversee the management of multiple sites").
 *  - The layer-picker entry carries the LIVE count — "Area Command (N)" — the gate's
 *    PEOC first-notice mitigation (the layer defaults OFF; the count is the notice).
 *  - The parent surface NEVER carries a person field ("commanded by" / officer /
 *    commander): App A #2 — area command is a FUNCTION of the participating
 *    organizations, and Ontario titles no "Area Commander" (#498).
 *  - "post" never appears unless the surface is an actual Area Command Post (App A #3);
 *    this slice renders none.
 *
 * ontarioImsVocabulary.lock.test.ts pins these strings + the forbidden drift terms;
 * areaCommandChildren.lock.test.tsx pins the rendered child rows (own name +
 * incident_number ONLY — never FIRE_COMPLEX_NAME or any foreign/backend value, the
 * #497 raw-CAP sentinel pattern). Changing any of this copy is founder authorization.
 */
/** Marker badge — semantic count of live child incidents (App A #2/#35), never the
 *  screen clusterer. */
export function areaCommandIncidentsBadge(incidentCount: number): string {
  return `+${incidentCount} ${incidentCount === 1 ? 'incident' : 'incidents'}`;
}
/** Expand-panel heading — "under area command" is verbatim App A #85 phrasing
 *  (grouping concept §4.6 / App A #2). */
export function incidentsUnderAreaCommandHeading(incidentCount: number): string {
  return `Incidents under area command (${incidentCount})`;
}
/** Layer-picker entry with the live Area Command count (the PEOC first-notice cue —
 *  visible even while the layer is toggled off). */
export function areaCommandLayerLabel(areaCommandCount: number): string {
  return `${AREA_COMMAND_HEADING} (${areaCommandCount})`;
}
/** The Area Command parent's timestamp prefix — an Area Command is ESTABLISHED
 *  (matches the shipped Establish-Area-Command affordance, ESTABLISH_AREA_COMMAND_LABEL,
 *  §4.6 / App A #2 — corrected from a stale §5.4 citation, #509: §5.4 is
 *  declaration-of-emergency, and ontario.ca §4.6 phrases the concept "area command is set
 *  up to manage multiple sites", so "Establish"/"Established" is the #493/#507
 *  gate-approved operator verb, not verbatim §4.6), never "Owned": ownership/command
 *  belongs to the child sites' own IMTs (App A #2, #498). */
export const AREA_COMMAND_ESTABLISHED_PREFIX = 'Established';

/**
 * #509 — the Area Command FIRST-NOTICE toast copy (workflow-completeness). Today an
 * operator only learns an Area Command was established by noticing the layer-picker row
 * count "Area Command (N)". A transient toast on a genuine mid-session establishment is
 * the first-notice cue. It REUSES the doctrine-locked vocabulary — no new term, no §4.3
 * exception: the prefix is AREA_COMMAND_ESTABLISHED_PREFIX ("Established" — the shipped
 * Establish-Area-Command verb, §4.6 / App A #2) and the noun is AREA_COMMAND_HEADING
 * ("Area Command", App A #2). Never "Owned"/"Declared" (ownership belongs to the child sites' IMTs, #498).
 * The generic form NEVER fabricates a name — it is used only when a poll surfaces several
 * new Area Commands at once (or the newly-established one can't be uniquely identified).
 * Auto-scanned by ontarioImsVocabulary.lock.test.ts (every label function) + pinned there.
 */
/** Name-bearing first-notice toast — "Established Area Command · <name>". */
export function areaCommandEstablishedToast(areaCommandName: string): string {
  return `${AREA_COMMAND_ESTABLISHED_PREFIX} ${AREA_COMMAND_HEADING} · ${areaCommandName}`;
}
/** Generic first-notice toast when the newly-established Area Command can't be uniquely
 *  named (multiple appeared in one poll / racey) — states the count, never a fabricated name. */
export function areaCommandEstablishedGenericToast(openCount: number): string {
  return `An ${AREA_COMMAND_HEADING} was established — ${openCount} now open.`;
}

/** Expand-panel empty state (shipped wording, #494; children are App A #35 incidents). */
export const NO_INCIDENTS_GROUPED_EMPTY = 'No incidents grouped yet.';

/**
 * R5 Area Command Slice 5 (#541, migration 0129) — the Area Command's OWN doctrine content
 * captured on the PARENT panel (Ontario IMS §4.6 / Appendix A #2, verbatim): area command
 * "has the responsibility to develop BROAD OBJECTIVES for their assigned area and set
 * PRIORITIES FOR THE SHARING OF CRITICAL RESOURCES across incident sites."
 *
 *  - The objectives heading KEEPS "broad" — the IAP-collision guard: bare "Objectives"
 *    collides with App A #40 *Incident objectives* (the per-incident tactical concept).
 *    NEVER "IAP" / "strategies and tactics" / operational-period framing on the parent
 *    (that is a CHILD's plan; §4.6 each grouped incident keeps its own IMT).
 *  - The resource heading KEEPS "critical" (App A #2). It is a PRIORITY STATEMENT — the
 *    copy NEVER says "assign" / "allocate" / "order to" / "task" / "direct" a child (§4.6:
 *    the area command "works with" the participating IMTs, it does not command them).
 *  - Both headings MUST byte-mirror the API SITREP copy (area-command-sitrep-copy.ts) so
 *    the panel + the aggregate SITREP read identically (dual-locked).
 *
 * ontarioImsVocabulary.lock.test.ts pins these strings + the forbidden drift terms.
 * Changing any of this copy is founder authorization.
 */
export const AREA_COMMAND_OBJECTIVES_HEADING = 'Broad objectives for the assigned area';
export const AREA_COMMAND_RESOURCE_PRIORITIES_HEADING =
  'Priorities for sharing critical resources across sites';
/** Capture-block empty states (mirror the SITREP empty lines). */
export const AREA_COMMAND_OBJECTIVES_EMPTY = 'No broad objectives set yet.';
export const AREA_COMMAND_RESOURCE_PRIORITIES_EMPTY = 'No resource-sharing priorities set yet.';
/** One-line doctrine framing captions — objectives are the Area Command's own; priorities
 *  are for SHARING, not commanding a child (§4.6 / App A #2). Forbidden-term-clean. */
export const AREA_COMMAND_OBJECTIVES_CAPTION =
  'Broad objectives the Area Command develops for its assigned area (Ontario IMS §4.6 / Appendix A #2).';
export const AREA_COMMAND_RESOURCE_PRIORITIES_CAPTION =
  'Priority statements for sharing critical resources across the incidents under area command (Appendix A #2). Each incident keeps its own command.';
/** Save affordances for the two capture blocks. */
export const SAVE_AREA_COMMAND_OBJECTIVES_LABEL = 'Save objectives';
export const SAVE_AREA_COMMAND_RESOURCE_PRIORITIES_LABEL = 'Save priorities';

/**
 * #450 — RESIDENT PUBLIC-INFORMATION channel (the #420b resident half, migration 0133).
 * Operator-facing copy, VERBATIM-grounded Ontario IMS §7.10 / Appendix A #62 (the public
 * information management section "develops and shares messages with the public directly … the
 * public information management section is led by the emergency information officer (EIO)") +
 * App A #23 (Emergency Information Officer / EIO). The municipality ISSUES ITS OWN release to
 * its OWN opted-in resident list.
 *
 * REGISTER GUARD (§4.3 / §7.10). This is NOT Alert Ready — the national wireless broadcast the
 * PEOC operates "on behalf of communities" (PERP §5.5.1), which a municipality only ever
 * REQUESTS via request_public_alert → PEOC (#497). So NO string here names or imitates the
 * reserved broadcast: the disclaimer distinguishes the municipal channel FUNCTIONALLY
 * (subscribers-only, not every device) and points to the escalation door in the REQUEST register
 * ("request a public alert" — §4.3 correct-domain-verbatim). "Alert Ready" / "wireless emergency
 * alert" / "En Alerte" / "broadcast intrusive alert" are FORBIDDEN on any operator string
 * (ontarioImsVocabulary.lock.test.ts), as is "PIO"/"Public Information Officer" (App A #23 — use
 * EIO). Changing this copy is founder authorization.
 */
export const PUBLIC_INFO_RELEASE_HEADING = 'Public information release';
export const PUBLISH_PUBLIC_INFO_LABEL = 'Publish public information';
export const PUBLISH_PUBLIC_INFO_BUSY_LABEL = 'Publishing…';
/** The author-of-record byline — App A #23 (EIO, never PIO). */
export const EIO_AUTHOR_OF_RECORD_LABEL = 'Emergency Information Officer (EIO) · author of record';
/** One-line framing for the compose surface. */
export const PUBLIC_INFO_COMPOSE_HELP =
  'Compose a plain-language release for residents subscribed to this community’s municipal ' +
  'notifications. You are the author of record — nothing is sent until you publish.';
/** Pre-fill affordance — seed the release from the incident’s Public Information Draft (evidence
 *  [n] markers + chrome are stripped so nothing operator-facing reaches a resident). */
export const PREFILL_FROM_PUBLIC_INFO_DRAFT_LABEL = 'Pre-fill from Public Information Draft';
/** Empty state when the incident has no in-zone community to target yet. */
export const NO_TARGET_COMMUNITY_EMPTY =
  'No community in this incident’s zone yet — declare a protective action, or pick a community, to publish a resident release.';
/** Body-empty guard state. */
export const PUBLIC_INFO_BODY_EMPTY = 'Write the resident release, then publish.';

/**
 * The register-guard line (GAP-450-B): names the CHANNEL + the REACH + the escalation door.
 * Distinguishes the municipal release from the province’s all-device broadcast WITHOUT naming
 * the reserved product (so it passes the Alert-Ready lock) — the correct doctrine posture (the
 * seat’s UI never renders "Alert Ready"). "request a public alert" is the §4.3 door (→ #497).
 */
export function residentReleaseRegisterGuard(community: string): string {
  return (
    `Published to ${community} residents subscribed to municipal notifications — subscribers only, ` +
    `not every device in the area. To reach all devices, request a public alert (issued by the province).`
  );
}

/** Publish confirmation — the count of residents the release reached. */
export function publishedToResidentsToast(community: string, recipientCount: number): string {
  return `Public information release published to ${recipientCount} ${community} resident${
    recipientCount === 1 ? '' : 's'
  }.`;
}

/** Publish confirmation when the community has no opted-in residents yet (nothing sent). */
export function noResidentsSubscribedToast(community: string): string {
  return `No ${community} residents are subscribed to municipal notifications yet — nothing sent.`;
}

/**
 * #328 Phase 1 — OUT-OF-APP OFFICIAL BRIEFING copy. Operator-facing, grounded in
 * Ontario IMS Guidance 2.0 (docs/ontario-ims-doctrine-reference.md):
 *   • §7.4 / Appendix A #19 — "briefing up": §7.4 names the executive assistant to
 *     the EOC director who "coordinates the 'briefing up' of incident information to
 *     senior and elected officials" (verbatim — the reference calls this the COP's
 *     Briefing-Up view); App A #19 (deputy IC) covers "briefing senior and elected
 *     officials". THIS is the doctrinal home for reaching a mayor / head of council.
 *   • Appendix A #21 — the emergency control group (ECG) "may not be directly
 *     involved in an EOC"; the mayor / head of council sits OUTSIDE the ops centre.
 *   • Appendix A #72 — the record-keeping (scribe) duty: every out-of-app contact
 *     is logged in-COP.
 *   • §2.2 — plain language.
 *
 * NOT the Liaison Officer (App A #46): that role is the contact for outside SUPPORTING
 * communities/organizations + requests for assistance, NOT up-briefing an elected
 * official. (Corrected at the #328 IMS doctrine gate; #498 doctrine-source discipline —
 * validated against the ontario.ca mirror §7.4 line 810-811 / App A #19, not a
 * fabricated "§7.11".)
 *
 * REGISTER GUARD. The register is INFORM/BRIEF, mirroring the emergency-declaration
 * block's "coordinates the record; it does not issue": the ops centre coordinates the
 * record and informs the official — it never DIRECTS them. HONESTY GUARD: an emailed or
 * phoned briefing is SENT, never proven received — no string here claims an
 * acknowledgement or read receipt the COP cannot observe. Changing this copy is founder
 * authorization (ontarioImsVocabulary.lock.test.ts pins presence + a forbidden-term guard,
 * incl. never "chat"/"DM" as the channel label).
 */
export const BRIEF_OFFICIAL_HEADING = 'Brief an official outside the ops centre';
export const BRIEF_OFFICIAL_CAPTION =
  'Briefing up (Ontario IMS §7.4 / Appendix A #19): the ops centre keeps a senior or elected official — a ' +
  'mayor, head of council, or the emergency control group — who is not in the EOC (Appendix A #21) informed ' +
  'by sharing the Situation Report. The ops centre coordinates the record and informs the official; it does ' +
  'not issue orders to them.';
/** Compose→send affordance — email the SITREP to officials outside the COP. */
export const EMAIL_SITREP_TO_OFFICIAL_LABEL = 'Email the Situation Report to…';
export const EMAIL_SITREP_BUSY_LABEL = 'Sending…';
/** Record-by-phone affordance (mirrors the declaration call_confirmed / call_notes register). */
export const RECORD_PHONE_CONTACT_LABEL = 'Record: reached by phone';
export const RECORD_PHONE_CONTACT_BUSY_LABEL = 'Recording…';
/** Ad-hoc recipient affordance — an official not in the registry (no pre-registration). */
export const BRIEF_OFFICIAL_ADHOC_EMAIL_PLACEHOLDER = 'official@municipality.ca';
export const BRIEF_OFFICIAL_ADHOC_ADD_LABEL = 'Add recipient';
/** Empty state — no saved officials to brief for this incident. */
export const BRIEF_OFFICIAL_NO_CONTACTS_EMPTY =
  'No saved officials for this incident yet — type an email address to brief someone outside the ops centre.';
/** Reached-by-phone field labels. */
export const REACHED_BY_PHONE_TIME_LABEL = 'Time reached (HH:MM)';
export const REACHED_BY_PHONE_NAME_LABEL = 'Official reached';
export const REACHED_BY_PHONE_NOTE_LABEL = 'What was conveyed (optional)';
/** Honesty note shown under a completed email send — sent ≠ acknowledged. */
export const BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE =
  'Sent to an external inbox — this is not proof it was read.';

/** "Sent HH:MM" confirmation for an emailed Situation Report (never "read"/"acknowledged"). */
export function sitrepEmailedLabel(hhmm: string): string {
  return `Situation Report sent ${hhmm}`;
}
/** Phone-contact record confirmation (mirrors the declaration "reached by phone" register). */
export function phoneContactRecordedLabel(hhmm: string): string {
  return `Recorded: reached by phone at ${hhmm}`;
}

/**
 * #568 — RECORD THE OFFICIAL'S RESPONSE (the paste-back half of the briefing-up loop; App A #72
 * Scribe). Closes the founder §1 workflow-completeness loop: a brief-official send records "sent",
 * and the official's reply is recorded back in-COP. The pre-ruled string set (founder 2026-07-06).
 *
 * HONESTY GUARD (pinned in ontarioImsVocabulary.lock.test.ts alongside the #549 SENT-not-ACK
 * note): recording a response is a SCRIBE note of what the official said — no string here claims
 * the official acknowledged, read, approved, or confirmed the Situation Report. The supporting
 * field labels are plain, functional microcopy (no IMS shorthand, no raw enums — the #328p1
 * "L3+" lesson), never doctrine titles.
 */
export const RECORD_OFFICIAL_RESPONSE_LABEL = 'Record response';
export const RECORD_OFFICIAL_RESPONSE_BUSY_LABEL = 'Recording…';
/** History-strip heading — plain language (the persisted record of briefs sent + responses). */
export const OFFICIAL_BRIEFING_HISTORY_HEADING = 'Briefing record';
export const OFFICIAL_BRIEFING_HISTORY_EMPTY =
  'No briefings recorded yet for this Situation Report.';
/** Record-response form labels (plain, functional — the channel the reply came back on, who
 *  responded, when, and what they said). */
export const RECORD_RESPONSE_CHANNEL_LABEL = 'Response received by';
export const RECORD_RESPONSE_CHANNEL_EMAIL = 'Email';
export const RECORD_RESPONSE_CHANNEL_PHONE = 'Phone';
export const RECORD_RESPONSE_OFFICIAL_LABEL = 'Official who responded';
export const RECORD_RESPONSE_TIME_LABEL = 'Time received (HH:MM)';
export const RECORD_RESPONSE_NOTE_LABEL = 'What the official said (optional)';
/** "Response recorded · HH:MM" — the confirmation after a response is logged. */
export function responseRecordedLabel(hhmm: string): string {
  return `Response recorded · ${hhmm}`;
}
/** "Response received — recorded by <seat>" — the history-strip attribution for a recorded
 *  response (seat = the app's existing seat-label mechanism, never a coined title). */
export function responseReceivedBySeatLabel(seat: string): string {
  return `Response received — recorded by ${seat}`;
}

/**
 * #458 / #459 — operator EVACUATION-ORDER correct + coordinating-attribution copy.
 *
 * REGISTER (Ontario IMS §4.2 + doctrine-reference §1.1). An operator-declared evac order
 * is a LIVING RECORD the COP records on behalf of a named ISSUER — so:
 *   - CORRECT / UPDATE THE RECORD, never "Amend" / "Re-issue": the COP records the order,
 *     it does not confer or re-confer it (records-not-confers).
 *   - COORDINATED BY <name>: a SECONDARY, support-register attribution (§4.2 coordination ≠
 *     command) for the Far-North tripartite case (a Band ISSUES; the PEOC COORDINATES). It
 *     is a past-participle VERB attribution — NEVER a "coordinating authority" NOUN, which
 *     would elevate the coordinator to a peer of the issuer (FN-dignity). The issuer stays
 *     primary + first on every surface; this label always renders AFTER "Issued by".
 * Pinned (+ a forbidden "coordinating authority" noun guard) in ontarioImsVocabulary.lock.test.ts.
 */
export const CORRECT_EVAC_ORDER_LABEL = 'Correct';
/** Save affordance for a metadata correction (name / population / notes / attribution). */
export const UPDATE_EVAC_RECORD_LABEL = 'Update the record';
export const UPDATE_EVAC_RECORD_BUSY_LABEL = 'Updating…';
/** The geometry-correction affordance — redraw the evacuation area on the map. */
export const CORRECT_EVAC_AREA_LABEL = 'Correct the area on the map';
/** Cancel an in-progress correction (drop back to the read-only detail). */
export const CANCEL_EVAC_CORRECTION_LABEL = 'Cancel';
/** SECONDARY attribution label for the coordinating body (rendered AFTER "Issued by"). */
export const COORDINATED_BY_LABEL = 'Coordinated by';
/** Optional coordinating-authority section heading on the declare + correct forms. */
export const COORDINATING_AUTHORITY_OPTIONAL_LABEL = 'Coordinated by (optional)';
/** The coordinator NAME-input field label — neutral "body", symmetric with the issuer's
 *  "Issuing body (named)". Routed through the module (not hardcoded) so the lock's noun
 *  guard actually covers it: a future edit to "Coordinating authority (named)" FAILS CI. */
export const COORDINATING_BODY_NAMED_LABEL = 'Coordinating body (named)';

/**
 * #462 — EVACUATION ACCOUNTING copy (the post-order §4.30 accounting surfaced FROM an
 * operator evacuation order).
 *
 * DOCTRINE GROUNDING (pre-build gate 2026-07-07, adversarially re-fetched — each verbatim
 * anchor carries a fetched Ontario/ISC citation; the founder-named terms are recorded AS
 * founder-named and are NEVER asserted as Ontario IMS 2.0 verbatim):
 *   VERBATIM (correct-domain, needs NO §4.3 exception):
 *     - "Evacuated" — ISC Ontario Region Emergency Management Plan, Regional profile:
 *       "…3,780 people evacuated…" (sac-isc.gc.ca/eng/1771600472528/1771600491104).
 *   FOUNDER-NAMED OPERATIONAL (Ontario IMS 2.0 carries NO evacuation-accounting vocabulary
 *   — the only ESS anchor is App A #29 "registration and inquiry services"; these five are
 *   honest operational terms, NOT doctrine verbatim):
 *     - "Remaining in community" (remaining) · "Awaiting flight" (awaiting-airlift) ·
 *       "Not yet accounted for" (unreachable) · "Evacuee accounting" (the panel title,
 *       reconciling the legacy "Headcount") · "Not yet counted" (the pending state).
 * FN-DIGNITY: the counts are the community's OWN ("residents accounted for", "our people
 * awaiting a flight") — never surveillant ("tracking residents"). No US/ICS term appears
 * (auto-scanned by ontarioImsVocabulary.lock.test.ts).
 */
export const EVAC_ACCOUNTING_HEADING = 'Evacuee accounting';
export const EVAC_ACCOUNTING_EVACUATED_LABEL = 'Evacuated';
export const EVAC_ACCOUNTING_REMAINING_LABEL = 'Remaining in community';
export const EVAC_ACCOUNTING_AWAITING_AIRLIFT_LABEL = 'Awaiting flight';
export const EVAC_ACCOUNTING_UNACCOUNTED_LABEL = 'Not yet accounted for';
/** Compact column headers (dense 4-up grid) — the same doctrine, abbreviated. */
export const EVAC_ACCOUNTING_REMAINING_SHORT = 'Remaining';
export const EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT = 'Awaiting flight';
export const EVAC_ACCOUNTING_UNACCOUNTED_SHORT = 'Unaccounted';
/** The pending / not-yet-counted state (unknown ≠ zero). */
export const EVAC_ACCOUNTING_PENDING_LABEL = 'Not yet counted';
export const EVAC_ACCOUNTING_PENDING_HELP =
  'In an evacuation zone for this incident — no headcount recorded yet. Not counted (not zero).';
/** Start / record affordances. */
export const START_EVAC_ACCOUNTING_LABEL = 'Start evacuee accounting';
export const START_EVAC_ACCOUNTING_BUSY_LABEL = 'Starting…';
export const RECORD_EVAC_ACCOUNTING_LABEL = 'Record';
/** #647 founder ruling (Christopher, 2026-07-13) — the zero-remaining fact, OBSERVED, not a
 *  completion the COP cannot see. `remaining = 0` means the last plane LEFT; it does NOT mean
 *  anyone ARRIVED (the #645 host attestation proves the sending count and the host count CAN
 *  disagree), and "accounted for" is ESS's word (Ontario IMS App A #29), not ours. So the words
 *  are the OBSERVED FACT — no "complete", no "all accounted for", no all-clear. The gate is
 *  UNCHANGED (still only reachable at zero pending AND zero remaining); the component appends
 *  "— counted <HH:MM>" from the latest headcount time. "COMPLETE is a word only Chief and Council
 *  may say, and only when the last person is HOME" (Kashechewan seat). Pinned by evacCompletionLabel.lock.test.tsx. */
export const EVAC_ACCOUNTING_COMPLETE_LABEL = '0 residents remaining in the community';
/** The incomplete banner when pending (uncounted) communities exist. */
export const EVAC_ACCOUNTING_INCOMPLETE_PREFIX = 'not yet counted — accounting incomplete';

/**
 * #485 — INCIDENT REACTIVATION vocabulary (reopening a Deactivated incident).
 *
 * The operator verb is "Reactivate / Reactivated" — the plain-language ANTONYM of the verbatim
 * lifecycle label "Deactivated" (AUTHORIZED_LIFECYCLE_LABELS.deactivated, §8 demobilization →
 * deactivation), hazard-AGNOSTIC (fire · flood · ice), colliding with no reserved doctrine
 * concept. It RETIRES the fire-only raw string "Reignite" that #535 shipped on the reopen
 * affordances (LinkToIncidentControl + IncidentManagementSection): "reignite" is wildfire jargon,
 * wrong for a flood/ice reopen, and was never in the lock. Registered as a §4.3 UI term in
 * docs/ontario-ims-doctrine-reference.md, FOUNDER-SIGNED (Christopher, 2026-07-10). The BACKEND
 * `reignited` field + the audit provenance stay (§4.2 backend prefers Ontario IMS but may keep an
 * internal token). "After-Action Report" is untouched (App A #1 verbatim — "Report", not "Review").
 *
 * incidentVocabulary.lock.test.ts pins these strings + a forbidden guard (no "Reignite" on a
 * reactivation surface, and the reserved "Declare" stays off this lifecycle affordance). Changing
 * this copy is founder authorization.
 */
/** The reopen verb (button + confirm prompt) and its past participle (the honesty line). */
export const REACTIVATE_INCIDENT_LABEL = 'Reactivate';
export const REACTIVATED_LABEL = 'Reactivated';
/** The read-time HONESTY line for a reopened incident. After a reopen closed_at is NULL, so the
 *  gap is derived from the advance_incident_lifecycle ledger, never from closed_at. Renders
 *  "Reactivated <t2> · was deactivated <t1>" — the component passes formatted relative times. */
export const WAS_DEACTIVATED_LABEL = 'was deactivated';
export function reactivatedGapLabel(reactivatedAt: string, deactivatedAt: string): string {
  return `${REACTIVATED_LABEL} ${reactivatedAt} · ${WAS_DEACTIVATED_LABEL} ${deactivatedAt}`;
}

/**
 * MUST-3 STALENESS WARNING — the founder-set threshold + the confirm copy. A WARNING, not a hard
 * block: a genuine re-emergence can be months later, so operator judgment governs. Never says
 * "declare" (reserved for declaration-of-emergency / authority orders) — a new incident is OWNed
 * (OWN_INCIDENT_LABEL). Default 14 days (founder range ~14–30).
 */
export const REACTIVATION_STALENESS_WARNING_DAYS = 14;
export function staleReactivationWarning(daysSinceDeactivation: number): string {
  return (
    `This incident was deactivated ${daysSinceDeactivation} ` +
    `${daysSinceDeactivation === 1 ? 'day' : 'days'} ago. Reactivate the same incident only if this ` +
    `is the same hazard re-emerging; otherwise own a new incident.`
  );
}

/**
 * MUST-1a FINDABILITY — the "Recently deactivated" surface (IncidentsPanel). Deactivated
 * incidents leave the open list + the map; this keeps them findable so a re-emergence reopens the
 * same thread instead of minting a duplicate. Open a row → the incident record → its Reactivate
 * control.
 */
export const RECENTLY_DEACTIVATED_HEADING = 'Recently deactivated';
export const RECENTLY_DEACTIVATED_EMPTY = 'No incidents deactivated in this window.';
export const RECENTLY_DEACTIVATED_HELP =
  'Deactivated incidents stay findable here so a re-emergence reopens the same thread instead of a ' +
  'new incident. Open one to reactivate it.';

/**
 * MUST-1b PROMOTE REOPEN-NUDGE — surfaced on the Own Incident affordance when a fresh
 * fire/hotspot detection lands within ~25 km of a recently-deactivated incident, BEFORE minting a
 * new one. A NUDGE, not a block: Own Incident stays available for a genuinely new hazard.
 */
export function reopenInsteadPrompt(incidentNumber: string): string {
  return `Reopen ${incidentNumber} instead?`;
}
export const REOPEN_NUDGE_HELP =
  'A recently deactivated incident is nearby. If this is the same hazard re-emerging, reactivate ' +
  'it to keep the whole thread instead of starting a new incident.';

/* NOTE — the post-reactivation SIDE-EFFECT CUES (open a new operational period / re-assume
 * Incident Command / re-confirm the Response Level) live as component-local copy in
 * IncidentManagementSection.tsx, NOT here: the "operational period" cue would trip this module's
 * Area-Command drift guard (ontarioImsVocabulary.lock.test.ts forbids "operational period" on any
 * incidentVocabulary string — it is a CHILD-incident concept that must never reach an Area Command
 * parent surface). Those cues render only on a normal reopened incident, where the term is correct. */

/**
 * The protective-action re-link path (LinkToIncidentControl) — linking an evac/CAP to a
 * deactivated incident reopens it. Same "Reactivate" doctrine as the lifecycle control (retires
 * the fire-only "reignite" that #478/#535 shipped here).
 */
export const WILL_REACTIVATE_ON_LINK_SUFFIX = ' · deactivated (will reactivate)';
export const LINKED_REACTIVATED_TOAST = 'Linked — the deactivated incident was reactivated.';
